<template>
  <div class="rd sp" :class="{ 'ui-compact': narrow }" :data-w="narrow ? 'sm' : 'lg'">
    <!-- Gornja traka -->
    <div class="sp-top no-print">
      <div class="sp-top__in">
        <img class="sp-top__logo" :src="logo" alt="Ordera" />
        <span class="sp-top__name">Ordera</span>
        <span class="sp-top__spacer"></span>
        <ui-segment class="sp-lang" :value="lang" :options="langOptions" :label="tt('language')" @input="setLang" />
        <button
          v-if="state === 'open' && !narrow"
          type="button"
          class="ui-icon-btn ui-icon-btn--plain"
          :title="tt('print')"
          :aria-label="tt('print')"
          @click="print"
        >
          <v-icon size="20">mdi-printer-outline</v-icon>
        </button>
      </div>
    </div>

    <div class="rd-page sp-page">
      <!-- Učitavanje -->
      <ui-state v-if="state === 'loading'" kind="loading" :tiles="3" :rows="5" />

      <!-- Nepoznat / opozvan / istekao / zatvoren link: bez ikakvih podataka o narudžbenici -->
      <ui-state v-else-if="terminal" kind="empty" lg :icon="terminal.icon" :title="terminal.title" :text="terminal.text">
        <ui-button v-if="state === 'error'" variant="primary" icon="mdi-refresh" @click="reload">{{ tt("retry") }}</ui-button>
      </ui-state>

      <!-- Odgovor poslat -->
      <div v-else-if="state === 'done'" class="ui-card sp-done">
        <div class="ui-empty ui-empty--lg">
          <span class="ui-empty__icon sp-done__icon" :class="{ 'is-ok': !rejected }">
            <v-icon size="30" color="currentColor">{{ rejected ? "mdi-close" : "mdi-check" }}</v-icon>
          </span>
          <b>{{ rejected ? tt("done_rejected_title") : tt("done_title") }}</b>
          <span>{{ rejected ? tt("done_text_rejected", { name: restaurantName }) : tt("done_text", { name: restaurantName }) }}</span>

          <div v-if="lastSummary && !rejected" class="ui-kpis sp-done__kpis">
            <div class="ui-kpi">
              <small>{{ tt("done_items") }}</small>
              <b>{{ lastSummary.items }}</b>
            </div>
            <div class="ui-kpi" :class="{ 'is-warn': lastSummary.changes }">
              <small>{{ tt("done_changes") }}</small>
              <b>{{ lastSummary.changes || tt("done_none") }}</b>
            </div>
            <div v-if="lastSummary.total !== null" class="ui-kpi">
              <small>{{ tt("done_total") }}</small>
              <b>{{ money(lastSummary.total) }} <span class="sp-cur">{{ cur }}</span></b>
            </div>
            <div v-if="lastSummary.delivery" class="ui-kpi">
              <small>{{ tt("done_delivery") }}</small>
              <b>{{ fmtDate(lastSummary.delivery) }}</b>
            </div>
          </div>

          <div class="ui-empty__actions">
            <ui-button icon="mdi-pencil-outline" @click="editAgain">{{ tt("done_edit") }}</ui-button>
          </div>
        </div>
      </div>

      <!-- Narudžbenica -->
      <div v-else-if="state === 'open' && doc" class="ui-screen sp-screen">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ tt("doc_title", { n: doc.label }) }}</h1>
            <p class="rd-head__sub">{{ subtitle }}</p>
          </div>
          <div class="rd-head__right">
            <ui-chip :tone="statusChip.tone" :icon="statusChip.icon" pill>{{ statusChip.label }}</ui-chip>
          </div>
        </div>

        <ui-banner v-if="returnNote" tone="warn" lg role="alert">
          <b>{{ tt("return_title") }}</b>
          <br />
          <span class="sp-pre">{{ returnNote }}</span>
        </ui-banner>

        <ui-banner v-if="draftRestored" tone="info" class="no-print">
          <span class="sp-draft">
            {{ tt("draft_restored") }}
            <button type="button" class="ui-link" @click="discardDraft">{{ tt("draft_reset") }}</button>
          </span>
        </ui-banner>

        <!-- Brojke -->
        <div class="ui-kpis sp-kpis">
          <div class="ui-kpi">
            <small>{{ tt("items_count") }}</small>
            <b>{{ rows.length }}</b>
          </div>
          <div v-if="requestedTotal !== null" class="ui-kpi">
            <small>{{ tt("est_value") }}</small>
            <b>{{ money(requestedTotal) }} <span class="sp-cur">{{ cur }}</span></b>
          </div>
          <div class="ui-kpi" :class="{ 'is-warn': changedCount }">
            <small>{{ tt("filter_changed") }}</small>
            <b>{{ changedCount }}</b>
          </div>
          <div v-if="expiresAt" class="ui-kpi">
            <small>{{ tt("respond_by") }}</small>
            <b>{{ fmtDate(expiresAt) }}</b>
            <em v-if="daysLeftText" :class="{ 'is-out': daysLeft <= 3 }">{{ daysLeftText }}</em>
          </div>
        </div>

        <!-- Strane i poruke -->
        <div class="ui-card">
          <div class="ui-card__body sp-parties">
            <ui-kv :rows="partyRows">
              <template #value="{ row }">
                <a v-if="row.key === 'phone'" :href="'tel:' + row.value" class="ui-link">{{ row.value }}</a>
                <template v-else>{{ row.value }}</template>
              </template>
            </ui-kv>
            <div v-if="doc.message || doc.notes" class="sp-msgs">
              <div v-if="doc.message" class="sp-msg">
                <small>{{ tt("restaurant_message") }}</small>
                <p class="sp-pre">{{ doc.message }}</p>
              </div>
              <div v-if="doc.notes" class="sp-msg">
                <small>{{ tt("order_note") }}</small>
                <p class="sp-pre">{{ doc.notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Stavke -->
        <section class="ui-card sp-items">
          <div class="ui-card__head">
            <b>{{ tt("items_title") }}</b>
            <small>{{ tt("review_items", { n: rows.length }) }}</small>
          </div>

          <div class="sp-tools no-print">
            <ui-chips v-model="filterMode" :options="filterOptions" :label="tt('items_title')" />
            <span class="ui-bar__spacer"></span>
            <ui-search v-if="rows.length > 8" v-model="search" :placeholder="tt('search')" />
            <ui-button size="sm" variant="ghost" icon="mdi-restore" :disabled="!changedCount" @click="resetAll">
              {{ tt("reset_all") }}
            </ui-button>
          </div>

          <div v-if="!filteredRows.length" class="ui-empty ui-empty--tight">
            <span class="ui-empty__icon"><v-icon size="22">mdi-magnify</v-icon></span>
            <span>{{ tt("no_results") }}</span>
          </div>

          <!-- Tabela (računar) -->
          <div v-else-if="!narrow" class="ui-table-wrap">
            <table class="ui-table sp-table">
              <thead>
                <tr>
                  <th style="width: 40px">#</th>
                  <th>{{ tt("col_item") }}</th>
                  <th class="r">{{ tt("col_requested") }}</th>
                  <th style="width: 170px">{{ tt("col_availability") }}</th>
                  <th class="r" style="width: 130px">{{ tt("col_qty") }}</th>
                  <th class="r" style="width: 130px">{{ tt("col_price", { cur }) }}</th>
                  <th class="r">{{ tt("col_amount") }}</th>
                  <th class="no-print" style="width: 44px"></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(it, idx) in filteredRows">
                  <tr :key="'r' + it.id" :class="{ 'is-off': it.availability === 'unavailable', 'sp-changed': isChanged(it) }">
                    <td class="ui-muted ui-num">{{ idx + 1 }}</td>
                    <td>
                      <b class="sp-name">{{ it.name || tt("item_fallback") }}</b>
                      <small v-if="it.supplier_code">{{ tt("code", { code: it.supplier_code }) }}</small>
                      <small v-if="it.substitute" class="ui-warn">{{ tt("substitute_label", { name: it.substitute }) }}</small>
                    </td>
                    <td class="r nw ui-num">
                      <b>{{ fmtNum(it.requested_amount) }}</b> {{ it.unit }}
                    </td>
                    <td>
                      <select
                        v-model="it.availability"
                        class="ui-input sp-in sp-in--sel"
                        :aria-label="tt('col_availability')"
                        @change="onAvailability(it)"
                      >
                        <option value="available">{{ tt("av_available") }}</option>
                        <option value="partial">{{ tt("av_partial") }}</option>
                        <option value="unavailable">{{ tt("av_unavailable") }}</option>
                      </select>
                    </td>
                    <td>
                      <input
                        v-model="it.offered_amount"
                        class="ui-input sp-in"
                        inputmode="decimal"
                        autocomplete="off"
                        :data-sp="'a' + it.id"
                        :disabled="it.availability === 'unavailable'"
                        :aria-label="tt('col_qty')"
                        :aria-invalid="String(!!errs(it).amount)"
                        @focus="$event.target.select()"
                        @blur="it.touched = true"
                        @keydown.enter.prevent="goNext('a', idx)"
                      />
                      <span v-if="errs(it).amount" class="ui-err sp-err">{{ errs(it).amount }}</span>
                    </td>
                    <td>
                      <input
                        v-model="it.unit_price"
                        class="ui-input sp-in"
                        inputmode="decimal"
                        autocomplete="off"
                        :data-sp="'p' + it.id"
                        :placeholder="it.expected_unit_price != null ? dec(it.expected_unit_price) : '0,00'"
                        :disabled="it.availability === 'unavailable'"
                        :aria-label="tt('col_price', { cur })"
                        :aria-invalid="String(!!errs(it).price)"
                        @focus="$event.target.select()"
                        @blur="it.touched = true"
                        @keydown.enter.prevent="goNext('p', idx)"
                      />
                      <span v-if="errs(it).price" class="ui-err sp-err">{{ errs(it).price }}</span>
                    </td>
                    <td class="r nw ui-num">
                      <b v-if="lineTotal(it) !== null">{{ money(lineTotal(it)) }}</b>
                      <span v-else class="ui-muted">—</span>
                    </td>
                    <td class="no-print ui-table__end">
                      <button
                        type="button"
                        class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
                        :title="tt('more_toggle')"
                        :aria-label="tt('more_toggle')"
                        :aria-expanded="String(!!it.more)"
                        @click="$set(it, 'more', !it.more)"
                      >
                        <v-icon size="18" :color="it.more || hasExtra(it) ? '#c2410c' : undefined">
                          {{ it.more ? "mdi-chevron-up" : "mdi-dots-horizontal" }}
                        </v-icon>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="it.more" :key="'x' + it.id" class="sp-extra">
                    <td></td>
                    <td colspan="7">
                      <div class="sp-extra__grid">
                        <div class="ui-field">
                          <label :for="'sc' + it.id">{{ tt("field_code") }}</label>
                          <input :id="'sc' + it.id" v-model="it.supplier_code" maxlength="100" />
                        </div>
                        <div class="ui-field">
                          <label :for="'ss' + it.id">{{ tt("field_substitute") }}</label>
                          <input :id="'ss' + it.id" v-model="it.substitute" maxlength="255" />
                        </div>
                        <div class="ui-field">
                          <label :for="'sn' + it.id">{{ tt("field_item_note") }}</label>
                          <input :id="'sn' + it.id" v-model="it.note" maxlength="500" />
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <p class="sp-hint no-print">{{ tt("shortcut_hint") }}</p>
          </div>

          <!-- Kartice (telefon) -->
          <div v-else class="ui-cardlist sp-cards">
            <div
              v-for="(it, idx) in filteredRows"
              :key="it.id"
              class="ui-item sp-item"
              :class="{ 'ui-item--warn': isChanged(it) && it.availability !== 'unavailable', 'ui-item--bad': it.availability === 'unavailable' }"
            >
              <div class="sp-item__head">
                <div class="sp-item__title">
                  <small class="ui-muted">#{{ idx + 1 }}<template v-if="it.supplier_code"> · {{ tt("code", { code: it.supplier_code }) }}</template></small>
                  <b class="sp-name" :class="{ 'is-off': it.availability === 'unavailable' }">{{ it.name || tt("item_fallback") }}</b>
                </div>
                <div class="sp-item__req">
                  <small>{{ tt("col_requested") }}</small>
                  <b class="ui-num">{{ fmtNum(it.requested_amount) }} {{ it.unit }}</b>
                </div>
              </div>

              <ui-segment
                v-model="it.availability"
                block
                :label="tt('col_availability')"
                :options="availOptions"
                @input="onAvailability(it)"
              />

              <div class="sp-item__fields">
                <div class="ui-field">
                  <label :for="'qa' + it.id">{{ tt("col_qty") }}<template v-if="it.unit"> ({{ it.unit }})</template></label>
                  <input
                    :id="'qa' + it.id"
                    v-model="it.offered_amount"
                    inputmode="decimal"
                    autocomplete="off"
                    :disabled="it.availability === 'unavailable'"
                    :aria-invalid="String(!!errs(it).amount)"
                    @focus="$event.target.select()"
                    @blur="it.touched = true"
                  />
                  <span v-if="errs(it).amount" class="ui-field__err">{{ errs(it).amount }}</span>
                </div>
                <div class="ui-field">
                  <label :for="'qp' + it.id">{{ tt("col_price", { cur }) }}</label>
                  <input
                    :id="'qp' + it.id"
                    v-model="it.unit_price"
                    inputmode="decimal"
                    autocomplete="off"
                    placeholder="0,00"
                    :disabled="it.availability === 'unavailable'"
                    :aria-invalid="String(!!errs(it).price)"
                    @focus="$event.target.select()"
                    @blur="it.touched = true"
                  />
                  <span v-if="errs(it).price" class="ui-field__err">{{ errs(it).price }}</span>
                  <span v-else-if="it.expected_unit_price != null" class="ui-field__hint">
                    {{ tt("price_hint_expected", { price: money(it.expected_unit_price) }) }}
                  </span>
                </div>
              </div>

              <div class="sp-item__foot">
                <button type="button" class="ui-text-btn" @click="$set(it, 'more', !it.more)">
                  <v-icon size="16" color="currentColor">{{ it.more ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                  {{ it.more ? tt("less") : tt("more_toggle") }}
                </button>
                <b v-if="lineTotal(it) !== null" class="ui-num">{{ money(lineTotal(it)) }} {{ cur }}</b>
              </div>

              <div v-if="it.more" class="ui-form">
                <div class="ui-field">
                  <label :for="'cc' + it.id">{{ tt("field_code") }}</label>
                  <input :id="'cc' + it.id" v-model="it.supplier_code" maxlength="100" />
                </div>
                <div class="ui-field">
                  <label :for="'cs' + it.id">{{ tt("field_substitute") }}</label>
                  <input :id="'cs' + it.id" v-model="it.substitute" maxlength="255" />
                </div>
                <div class="ui-field">
                  <label :for="'cn' + it.id">{{ tt("field_item_note") }}</label>
                  <input :id="'cn' + it.id" v-model="it.note" maxlength="500" />
                </div>
              </div>
            </div>
          </div>

          <!-- Zbir -->
          <div class="sp-totals">
            <div v-if="requestedTotal !== null" class="sp-totals__row">
              <span class="ui-muted">{{ tt("requested_est") }}</span>
              <b class="ui-num">{{ money(requestedTotal) }} {{ cur }}</b>
            </div>
            <div v-if="offeredTotal !== null" class="sp-totals__row sp-totals__row--main">
              <span>{{ tt("your_offer") }}</span>
              <b class="ui-num">{{ money(offeredTotal) }} {{ cur }}</b>
              <ui-chip v-if="diffTotal !== null && Math.abs(diffTotal) >= 0.005" :tone="diffTotal > 0 ? 'warn' : 'blue'">
                {{ diffTotal > 0 ? "+" : "" }}{{ money(diffTotal) }}
              </ui-chip>
            </div>
            <p class="sp-totals__note ui-muted">
              {{ offeredTotal === null ? tt("enter_prices") : form_.prices_include_vat ? tt("vat_incl_note") : tt("vat_excl_note") }}
            </p>
          </div>
        </section>

        <!-- Isporuka i uslovi -->
        <section class="ui-card">
          <div class="ui-card__head">
            <b>{{ tt("delivery_title") }}</b>
          </div>
          <div class="ui-card__body ui-form">
            <div class="ui-grid">
              <div class="ui-field">
                <label for="sp-date">{{ tt("delivery_date") }}</label>
                <input id="sp-date" v-model="form_.delivery_date" type="date" :min="today" />
                <ui-chips v-model="form_.delivery_date" class="sp-date-chips no-print" :options="dateChips" :label="tt('delivery_date')" />
                <span v-if="form_.delivery_date" class="ui-field__hint">{{ weekday(form_.delivery_date) }}</span>
              </div>
              <div class="ui-field">
                <label id="sp-vat-l">{{ tt("prices") }}</label>
                <ui-segment v-model="form_.prices_include_vat" class="sp-vat" labelledby="sp-vat-l" :options="vatOptions" />
                <label for="sp-contact" class="sp-gap">{{ tt("contact") }}</label>
                <input id="sp-contact" v-model="form_.contact_name" :placeholder="tt('contact_ph')" maxlength="150" autocomplete="name" />
              </div>
              <div class="ui-field ui-field--full">
                <label for="sp-note">{{ tt("note") }}</label>
                <textarea id="sp-note" v-model="form_.note" class="sp-textarea" rows="3" maxlength="1000" :placeholder="tt('note_ph')"></textarea>
              </div>
            </div>
          </div>
        </section>

        <ui-banner v-if="errorText" role="alert" class="no-print">{{ errorText }}</ui-banner>
        <p class="sp-foot ui-muted no-print">{{ tt("footer_hint") }}</p>
      </div>

      <footer class="sp-copy">
        ©{{ new Date().getFullYear() }}
        <a target="_blank" rel="noopener" href="https://bitlab.rs/">BitLab d.o.o</a>
        {{ $t("all_rights_reserved") }}.
        <a target="_blank" rel="noopener" href="https://privacy.ordera.app/">{{ $t("privacy_policy") }}</a>
      </footer>
    </div>

    <!-- Fiksna traka sa radnjama -->
    <div v-if="state === 'open' && doc" class="sp-bar no-print">
      <div class="sp-bar__in">
        <div class="sp-bar__sum">
          <small>{{ offeredTotal !== null ? tt("your_offer") : tt("items_count") }}</small>
          <b class="ui-num">{{ offeredTotal !== null ? money(offeredTotal) + " " + cur : rows.length }}</b>
        </div>
        <span class="sp-bar__spacer"></span>
        <ui-button class="sp-reject" :disabled="!!busy" @click="rejectDialog = true">
          <span>{{ tt("act_reject") }}</span>
        </ui-button>
        <ui-button variant="primary" icon="mdi-check" :disabled="!!busy" @click="review">
          {{ changedCount ? tt("act_send_changes") : tt("act_confirm") }}
        </ui-button>
      </div>
    </div>

    <!-- Pregled prije slanja -->
    <ui-sheet v-model="reviewDialog" :title="tt('review_title')">
      <div class="sp-sheet">
        <div class="ui-chips">
          <ui-chip>{{ tt("review_items", { n: rows.length }) }}</ui-chip>
          <ui-chip :tone="changedCount ? 'warn' : 'ok'">
            {{ changedCount ? tt("review_changes", { n: changedCount }) : tt("review_all_ok") }}
          </ui-chip>
          <ui-chip v-if="offeredTotal !== null" tone="accent">{{ money(offeredTotal) }} {{ cur }}</ui-chip>
        </div>

        <ui-banner v-if="!form_.delivery_date" tone="warn">{{ tt("review_no_date") }}</ui-banner>
        <ui-banner v-if="noPriceCount" tone="warn">{{ tt("review_no_price", { n: noPriceCount }) }}</ui-banner>

        <ui-section v-if="changedRows.length" :title="tt('review_diff_title')">
          <ul class="sp-changes">
            <li v-for="it in changedRows" :key="it.id">
              <b>{{ it.name }}</b>
              <span v-for="(c, i) in describeChanges(it)" :key="i">{{ c }}</span>
            </li>
          </ul>
        </ui-section>
        <p v-else class="ui-muted">{{ tt("review_all_exact") }}</p>

        <ui-banner v-if="errorText" role="alert">{{ errorText }}</ui-banner>
      </div>
      <template #footer>
        <div class="sp-sheet-actions">
          <ui-button @click="reviewDialog = false">{{ tt("review_back") }}</ui-button>
          <ui-button variant="primary" icon="mdi-send" :disabled="!!busy" @click="submit('respond')">
            {{ tt("review_send") }}
          </ui-button>
        </div>
      </template>
    </ui-sheet>

    <!-- Odbijanje -->
    <ui-sheet v-model="rejectDialog" :title="tt('reject_title')">
      <div class="sp-sheet">
        <p class="ui-muted">{{ tt("reject_text") }}</p>
        <div class="ui-field">
          <label for="sp-reject">{{ tt("reject_reason") }}</label>
          <textarea id="sp-reject" v-model="rejectNote" class="sp-textarea" rows="3" maxlength="1000"></textarea>
        </div>
        <ui-banner v-if="errorText" role="alert">{{ errorText }}</ui-banner>
      </div>
      <template #footer>
        <div class="sp-sheet-actions">
          <ui-button @click="rejectDialog = false">{{ tt("review_back") }}</ui-button>
          <ui-button variant="danger" :disabled="!!busy" @click="submit('reject')">{{ tt("reject_confirm") }}</ui-button>
        </div>
      </template>
    </ui-sheet>
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import supplierPoService from "@/services/supplierPo";

// Prihvata "1,5", "1.5" i "1,234.50" / "1.234,50"
const num = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  let s = String(v).trim().replace(/\s/g, "");
  const c = s.lastIndexOf(",");
  const d = s.lastIndexOf(".");
  if (c > -1 && d > -1) s = c > d ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  else s = s.replace(",", ".");
  return Number(s);
};
const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return iso(d);
};

const LANGS = [
  { value: "rs-lat", label: "LAT", loc: "sr-Latn-RS" },
  { value: "rs", label: "ЋИР", loc: "sr-Cyrl-RS" },
  { value: "bs", label: "BS", loc: "bs-Latn-BA" },
  { value: "hr", label: "HR", loc: "hr-HR" },
  { value: "en", label: "EN", loc: "en-GB" },
];
const LANG_KEY = "supplier-po-lang";

export default {
  name: "SupplierPo",
  data: () => ({
    state: "loading", // loading | open | done | notfound | revoked | expired | closed | error
    doc: null,
    rows: [],
    initial: null,
    returnNote: null,
    prevVersion: null,
    expiresAt: null,
    form_: { delivery_date: "", contact_name: "", note: "", prices_include_vat: false },
    busy: null,
    error: "", // kljuc poruke (supplier_page.*)
    errorRaw: "", // poruka sa servera
    rejected: false,
    rejectDialog: false,
    reviewDialog: false,
    rejectNote: "",
    search: "",
    filterMode: "all", // all | changed | noprice
    showErrors: false,
    draftRestored: false,
    lastSummary: null,
    narrow: false,
    lang: "rs-lat",
    prevLocale: null,
    prevTitle: "",
    today: iso(new Date()),
    logo: (process.env.BASE_URL || "/") + "o.svg",
    saveTimer: null,
  }),
  computed: {
    token() {
      return this.$route.params.token;
    },
    langOptions() {
      return LANGS.map((l) => ({ value: l.value, label: l.label }));
    },
    numLocale() {
      return (LANGS.find((l) => l.value === this.lang) || LANGS[0]).loc;
    },
    cur() {
      return (this.doc && this.doc.currency) || "KM";
    },
    restaurantName() {
      return (this.doc && this.doc.restaurant && this.doc.restaurant.name) || this.tt("restaurant_fallback");
    },
    subtitle() {
      const parts = [this.restaurantName];
      if (this.doc && this.doc.date) parts.push(this.tt("doc_from", { date: this.fmtDate(this.doc.date) }));
      return parts.join(" · ");
    },
    partyRows() {
      const r = (this.doc && this.doc.restaurant) || {};
      const rows = [{ key: "party", label: this.tt("ordering_party"), value: this.restaurantName }];
      const addr = [r.address, r.city].filter(Boolean).join(", ");
      if (addr) rows.push({ key: "address", label: this.tt("address"), value: addr });
      if (r.phone) rows.push({ key: "phone", label: this.tt("phone"), value: r.phone });
      if (this.doc && this.doc.supplier_name) rows.push({ key: "supplier", label: this.tt("supplier"), value: this.doc.supplier_name });
      return rows;
    },
    daysLeft() {
      if (!this.expiresAt) return null;
      const ms = new Date(this.expiresAt).getTime() - Date.now();
      return isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 86400000));
    },
    daysLeftText() {
      const n = this.daysLeft;
      if (n === null) return "";
      if (n === 0) return this.tt("expires_today");
      return n === 1 ? this.tt("days_left_one") : this.tt("days_left_many", { n });
    },
    dateChips() {
      return [
        { value: addDays(1), label: this.tt("chip_tomorrow") },
        { value: addDays(2), label: this.tt("chip_day_after") },
        { value: addDays(3), label: this.tt("chip_3days") },
      ];
    },
    vatOptions() {
      return [
        { value: false, label: this.tt("vat_excl") },
        { value: true, label: this.tt("vat_incl") },
      ];
    },
    availOptions() {
      return [
        { value: "available", label: this.tt("av_available") },
        { value: "partial", label: this.tt("av_partial_short") },
        { value: "unavailable", label: this.tt("av_unavailable") },
      ];
    },
    statusChip() {
      if (this.returnNote) return { label: this.tt("status_return"), tone: "warn", icon: "mdi-undo-variant" };
      if (this.prevVersion) return { label: this.tt("status_replied"), tone: "blue", icon: "mdi-check" };
      return { label: this.tt("status_waiting"), tone: "accent", icon: "mdi-clock-outline" };
    },
    changedRows() {
      return this.rows.filter((r) => this.isChanged(r));
    },
    changedCount() {
      return this.changedRows.length;
    },
    noPriceCount() {
      return this.rows.filter((r) => this.missingPrice(r)).length;
    },
    filterOptions() {
      return [
        { value: "all", label: this.tt("filter_all"), count: this.rows.length },
        { value: "changed", label: this.tt("filter_changed"), count: this.changedCount, dot: "#d97706" },
        { value: "noprice", label: this.tt("filter_no_price"), count: this.noPriceCount },
      ];
    },
    filteredRows() {
      const q = (this.search || "").trim().toLowerCase();
      return this.rows.filter((r) => {
        if (this.filterMode === "changed" && !this.isChanged(r)) return false;
        if (this.filterMode === "noprice" && !this.missingPrice(r)) return false;
        if (!q) return true;
        return (r.name || "").toLowerCase().includes(q) || (r.supplier_code || "").toLowerCase().includes(q);
      });
    },
    requestedTotal() {
      let sum = 0;
      let any = false;
      this.rows.forEach((r) => {
        if (r.expected_unit_price != null) {
          sum += Number(r.requested_amount) * Number(r.expected_unit_price);
          any = true;
        }
      });
      return any ? sum : null;
    },
    offeredTotal() {
      let sum = 0;
      let any = false;
      this.rows.forEach((r) => {
        const t = this.lineTotal(r);
        if (t !== null) {
          sum += t;
          any = true;
        }
      });
      return any ? sum : null;
    },
    diffTotal() {
      if (this.offeredTotal === null || this.requestedTotal === null) return null;
      return this.offeredTotal - this.requestedTotal;
    },
    errorText() {
      return this.errorRaw || (this.error ? this.tt(this.error) : "");
    },
    terminal() {
      const map = {
        notfound: { icon: "mdi-link-off", key: "t_notfound" },
        revoked: { icon: "mdi-cancel", key: "t_revoked" },
        expired: { icon: "mdi-clock-alert-outline", key: "t_expired" },
        closed: { icon: "mdi-lock-outline", key: "t_closed" },
        error: { icon: "mdi-alert-circle-outline", key: "t_error" },
      };
      const m = map[this.state];
      return m ? { icon: m.icon, title: this.tt(m.key + "_title"), text: this.tt(m.key + "_text") } : null;
    },
  },
  watch: {
    rows: { deep: true, handler: "queueDraft" },
    form_: { deep: true, handler: "queueDraft" },
    doc(d) {
      if (d) document.title = `${this.tt("doc_title", { n: d.label })} · Ordera`;
    },
  },
  created() {
    this.prevLocale = this.$i18n.locale;
    this.prevTitle = document.title;
    this.applyLang(this.initialLang());
    this.load();
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    clearTimeout(this.saveTimer);
    // jezik ove stranice ne smije promijeniti jezik aplikacije prijavljenom korisniku
    if (this.prevLocale) this.$i18n.locale = this.prevLocale;
    document.title = this.prevTitle;
  },
  methods: {
    tt(key, params) {
      return this.$t("supplier_page." + key, params);
    },
    // --- jezik ---
    initialLang() {
      const valid = (v) => LANGS.some((l) => l.value === v);
      const q = this.$route.query && this.$route.query.lang;
      if (valid(q)) return q;
      try {
        const saved = localStorage.getItem(LANG_KEY);
        if (valid(saved)) return saved;
      } catch (e) {
        /* nije bitno */
      }
      return "rs-lat";
    },
    applyLang(code) {
      this.lang = code;
      this.$i18n.locale = code;
    },
    setLang(code) {
      this.applyLang(code);
      try {
        localStorage.setItem(LANG_KEY, code);
      } catch (e) {
        /* nije bitno */
      }
      if (this.doc) document.title = `${this.tt("doc_title", { n: this.doc.label })} · Ordera`;
    },
    onResize() {
      this.narrow = window.innerWidth < 720;
    },
    print() {
      window.print();
    },
    reload() {
      this.state = "loading";
      this.load();
    },
    // --- formatiranje ---
    // broj za polje unosa: decimalni zarez (osim engleskog); parsiranje prihvata i zarez i tačku
    dec(v) {
      return this.lang === "en" ? String(v) : String(v).replace(".", ",");
    },
    money(v) {
      const n = Number(v);
      return isNaN(n) ? v : n.toLocaleString(this.numLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtDate(v) {
      if (!v) return "";
      const d = new Date(String(v).length === 10 ? v + "T00:00:00" : v);
      return isNaN(d) ? v : d.toLocaleDateString(this.numLocale, { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    weekday(v) {
      const d = new Date(v + "T00:00:00");
      return isNaN(d) ? "" : d.toLocaleDateString(this.numLocale, { weekday: "long", day: "numeric", month: "long" });
    },
    fmtNum(v) {
      const n = Number(v);
      return isNaN(n) ? v : n.toLocaleString(this.numLocale, { maximumFractionDigits: 3 });
    },
    // --- logika redova ---
    hasExtra(it) {
      return !!((it.supplier_code && it.supplier_code !== it._origCode) || it.substitute || it.note);
    },
    missingPrice(r) {
      return r.availability !== "unavailable" && (r.unit_price === "" || r.unit_price == null);
    },
    priceDiffers(r) {
      if (r.expected_unit_price == null || r.unit_price === "") return false;
      const p = num(r.unit_price);
      return !isNaN(p) && Math.abs(p - Number(r.expected_unit_price)) > 0.00001;
    },
    isChanged(r) {
      return (
        r.availability !== "available" ||
        num(r.offered_amount) !== Number(r.requested_amount) ||
        this.priceDiffers(r) ||
        !!(r.substitute && r.substitute.trim() !== "")
      );
    },
    describeChanges(r) {
      const out = [];
      const unit = r.unit || "";
      if (r.availability === "unavailable") return [this.tt("change_unavailable")];
      if (r.availability === "partial") {
        out.push(this.tt("change_partial", { offered: this.fmtNum(num(r.offered_amount)), requested: this.fmtNum(r.requested_amount), unit }));
      } else if (num(r.offered_amount) !== Number(r.requested_amount)) {
        out.push(this.tt("change_qty", { from: this.fmtNum(r.requested_amount), to: this.fmtNum(num(r.offered_amount)), unit }));
      }
      if (this.priceDiffers(r)) {
        out.push(this.tt("change_price", { from: this.money(r.expected_unit_price), to: this.money(num(r.unit_price)), cur: this.cur }));
      }
      if (r.substitute && r.substitute.trim()) out.push(this.tt("change_substitute", { name: r.substitute }));
      return out;
    },
    lineTotal(r) {
      if (r.availability === "unavailable") return null;
      const q = num(r.offered_amount);
      const p = num(r.unit_price);
      if (isNaN(q) || isNaN(p)) return null;
      return q * p;
    },
    rawErrors(r) {
      const e = { amount: "", price: "" };
      if (r.availability !== "unavailable") {
        const q = num(r.offered_amount);
        if (isNaN(q) || q < 0) e.amount = "err_number";
        else if (r.availability === "partial" && q <= 0) e.amount = "err_partial";
        else if (r.availability === "available" && q <= 0) e.amount = "err_positive";
      }
      if (r.unit_price !== "" && r.unit_price != null) {
        const p = num(r.unit_price);
        if (isNaN(p) || p < 0) e.price = "err_number";
      }
      return e;
    },
    errs(r) {
      if (!this.showErrors && !r.touched) return { amount: "", price: "" };
      const e = this.rawErrors(r);
      return { amount: e.amount ? this.tt(e.amount) : "", price: e.price ? this.tt(e.price) : "" };
    },
    onAvailability(it) {
      if (it.availability === "unavailable") it.offered_amount = "0";
      else if (it.availability === "available") {
        if (isNaN(num(it.offered_amount)) || num(it.offered_amount) === 0) it.offered_amount = this.dec(it.requested_amount);
      } else if (it.availability === "partial" && num(it.offered_amount) === Number(it.requested_amount)) {
        it.offered_amount = "";
        this.$nextTick(() => {
          const el = document.querySelector(`input[data-sp="a${it.id}"]`);
          if (el) el.focus();
        });
      }
    },
    resetAll() {
      this.rows.forEach((r) => {
        r.availability = "available";
        r.offered_amount = this.dec(r.requested_amount);
        r.unit_price = r.expected_unit_price != null ? this.dec(r.expected_unit_price) : r.unit_price;
        r.substitute = "";
        r.touched = false;
      });
      this.showErrors = false;
    },
    // Enter prelazi na isto polje u sljedećem redu (kao u tabeli), preskače isključene redove
    goNext(kind, idx) {
      for (let i = idx + 1; i < this.filteredRows.length; i++) {
        const el = document.querySelector(`input[data-sp="${kind}${this.filteredRows[i].id}"]`);
        if (el && !el.disabled) {
          el.focus();
          if (el.select) el.select();
          return;
        }
      }
      const cur = document.activeElement;
      if (cur && cur.blur) cur.blur();
    },
    // --- učitavanje ---
    buildRows(items) {
      return items.map((i) => {
        const prev = i.previous;
        return {
          id: i.id,
          name: i.name,
          unit: i.unit,
          requested_amount: i.requested_amount,
          expected_unit_price: i.expected_unit_price,
          availability: prev ? prev.availability : "available",
          offered_amount: this.dec(prev ? prev.offered_amount : i.requested_amount),
          unit_price:
            prev && prev.unit_price != null
              ? this.dec(prev.unit_price)
              : i.expected_unit_price != null
              ? this.dec(i.expected_unit_price)
              : "",
          supplier_code: (prev && prev.supplier_code) || i.supplier_code || "",
          _origCode: i.supplier_code || "",
          substitute: (prev && prev.substitute) || "",
          note: (prev && prev.note) || "",
          more: !!(prev && (prev.substitute || prev.note)),
          touched: false,
        };
      });
    },
    async load() {
      try {
        const res = await supplierPoService.get(this.token);
        const p = res.data;
        this.doc = p.document;
        this.expiresAt = p.expires_at;
        if (p.state !== "open") {
          this.state = p.state;
          return;
        }
        this.returnNote = p.return_note;
        this.prevVersion = p.response ? p.response.version : null;
        this.rows = this.buildRows(p.document.items);
        if (p.response) {
          this.form_ = {
            delivery_date: p.response.delivery_date || "",
            prices_include_vat: !!p.response.prices_include_vat,
            contact_name: p.response.contact_name || "",
            note: p.response.note || "",
          };
        }
        this.initial = JSON.parse(JSON.stringify({ rows: this.rows, form_: this.form_ }));
        this.restoreDraft();
        this.state = "open";
        // otvaranje se evidentira tek kad se stranica stvarno prikaže (ne pri prefetch-u mejl skenera)
        supplierPoService.opened(this.token).catch(() => {});
      } catch (e) {
        const r = e.response;
        if (r && r.status === 404) this.state = "notfound";
        else if (r && r.status === 410) this.state = (r.data && r.data.state) || "expired";
        else this.state = "error";
      }
    },
    // --- nacrt (čuva se u pregledniku da unos ne nestane pri osvježavanju) ---
    draftKey() {
      return "supplier-po-draft:" + this.token;
    },
    snapshot(rows, form) {
      return {
        form_: {
          delivery_date: form.delivery_date,
          contact_name: form.contact_name,
          note: form.note,
          prices_include_vat: form.prices_include_vat,
        },
        rows: rows.map((r) => ({
          id: r.id,
          availability: r.availability,
          offered_amount: r.offered_amount,
          unit_price: r.unit_price,
          supplier_code: r.supplier_code,
          substitute: r.substitute,
          note: r.note,
        })),
      };
    },
    queueDraft() {
      if (this.state !== "open") return;
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        try {
          const data = this.snapshot(this.rows, this.form_);
          // ništa izmijenjeno u odnosu na ono što je server poslao: nema šta da se čuva
          if (this.initial && JSON.stringify(data) === JSON.stringify(this.snapshot(this.initial.rows, this.initial.form_))) {
            localStorage.removeItem(this.draftKey());
            return;
          }
          localStorage.setItem(this.draftKey(), JSON.stringify(data));
        } catch (e) {
          /* privatni prozor ili blokiran storage: nacrt se jednostavno ne čuva */
        }
      }, 500);
    },
    restoreDraft() {
      try {
        const raw = localStorage.getItem(this.draftKey());
        if (!raw) return;
        const d = JSON.parse(raw);
        const byId = {};
        (d.rows || []).forEach((r) => (byId[r.id] = r));
        let touched = false;
        this.rows.forEach((r) => {
          const s = byId[r.id];
          if (!s) return;
          ["availability", "offered_amount", "unit_price", "supplier_code", "substitute", "note"].forEach((k) => {
            if (s[k] !== undefined && s[k] !== r[k]) {
              r[k] = s[k];
              touched = true;
            }
          });
          if (r.substitute || r.note) r.more = true;
        });
        if (d.form_) {
          Object.keys(this.form_).forEach((k) => {
            if (d.form_[k] !== undefined && d.form_[k] !== this.form_[k]) {
              this.form_[k] = d.form_[k];
              touched = true;
            }
          });
        }
        this.draftRestored = touched;
      } catch (e) {
        /* neispravan nacrt se ignoriše */
      }
    },
    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey());
      } catch (e) {
        /* nije bitno */
      }
    },
    discardDraft() {
      this.clearDraft();
      this.rows = this.buildRows(this.doc.items);
      this.form_ = JSON.parse(JSON.stringify(this.initial.form_));
      this.draftRestored = false;
      this.showErrors = false;
    },
    // --- slanje ---
    review() {
      this.error = "";
      this.errorRaw = "";
      this.showErrors = true;
      const bad = this.rows.find((r) => {
        const e = this.rawErrors(r);
        return e.amount || e.price;
      });
      if (bad) {
        this.search = "";
        this.filterMode = "all";
        this.error = "err_check_fields";
        this.$nextTick(() => {
          const el = document.querySelector('.sp [aria-invalid="true"]');
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.focus({ preventScroll: true });
          }
        });
        return;
      }
      this.reviewDialog = true;
    },
    async submit(action) {
      this.error = "";
      this.errorRaw = "";
      const payload =
        action === "reject"
          ? { action: "reject", note: this.rejectNote || null, contact_name: this.form_.contact_name || null }
          : {
              action: "respond",
              delivery_date: this.form_.delivery_date || null,
              prices_include_vat: !!this.form_.prices_include_vat,
              contact_name: this.form_.contact_name || null,
              note: this.form_.note || null,
              items: this.rows.map((r) => ({
                transaction_id: r.id,
                offered_amount: r.availability === "unavailable" ? 0 : num(r.offered_amount),
                unit_price: r.unit_price === "" || r.unit_price == null ? null : num(r.unit_price),
                availability: r.availability,
                supplier_code: r.supplier_code || null,
                substitute: r.substitute || null,
                note: r.note || null,
              })),
            };
      this.busy = action;
      try {
        await supplierPoService.respond(this.token, payload);
        this.rejected = action === "reject";
        this.lastSummary = {
          items: this.rows.length,
          changes: this.changedCount,
          total: this.offeredTotal,
          delivery: this.form_.delivery_date || null,
        };
        this.clearDraft();
        this.rejectDialog = false;
        this.reviewDialog = false;
        this.state = "done";
        window.scrollTo(0, 0);
      } catch (e) {
        const r = e.response;
        if (r && (r.status === 410 || r.status === 404)) {
          this.rejectDialog = false;
          this.reviewDialog = false;
          this.state = r.status === 404 ? "notfound" : (r.data && r.data.state) || "expired";
        } else if (r && r.status === 422) {
          const errs = (r.data && r.data.errors) || {};
          const first = Object.values(errs)[0];
          if (first && first[0]) this.errorRaw = first[0];
          else this.error = "err_422";
        } else if (r && r.status === 429) {
          this.error = "err_429";
        } else {
          this.error = "err_generic";
        }
      } finally {
        this.busy = null;
      }
    },
    editAgain() {
      this.state = "open";
      this.prevVersion = (this.prevVersion || 0) + 1;
      this.returnNote = null;
      window.scrollTo(0, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Stranica dobavljača: elementi i tokeni novog izgleda (.rd + ui-*), kao Magacin i izvještaji. */
.sp.rd {
  min-height: 100vh;
  padding-bottom: 92px;
}

/* gornja traka: logo i jezik */
.sp-top {
  background: #fff8f5;
  border-bottom: 1px solid var(--rm-border);

  &__in {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    max-width: 1120px;
    height: 60px;
    margin: 0 auto;
    padding: 0 24px;
  }

  &__logo {
    width: 34px;
    height: 34px;
  }

  &__name {
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  &__spacer {
    flex: 1;
  }
}

.sp-lang {
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.sp-page {
  max-width: 1120px;
  margin: 0 auto;
  padding-top: 12px;
}

.sp-copy {
  margin-top: 24px;
  text-align: center;
  font-size: 12.5px;
  color: var(--rm-muted);

  a {
    color: var(--rm-accent-strong);
    text-decoration: none;
  }
}

.sp-screen {
  gap: 16px;
}

.sp-pre {
  white-space: pre-wrap;
}

.sp-cur {
  font-size: 13px;
  font-weight: 600;
  color: var(--rm-muted);
}

.sp-draft {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  align-items: center;
}

/* strane + poruke */
.sp-parties {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px 24px;
  align-items: start;
}

.sp-parties .ui-kv {
  border: 0;
  padding: 0;
}

.sp-vat {
  justify-self: start;
}

.sp-msgs {
  display: grid;
  gap: 10px;
}

.sp-msg {
  padding: 10px 14px;
  border-left: 3px solid var(--rm-accent);
  border-radius: 0 10px 10px 0;
  background: var(--rm-accent-soft);

  small {
    color: var(--rm-accent-strong);
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  p {
    margin-top: 2px;
  }
}

/* alatke iznad stavki */
.sp-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--rm-divider);
}

/* tabela stavki */
.sp-table {
  .sp-in {
    width: 100%;
    min-width: 72px;
    text-align: right;

    &--sel {
      appearance: none;
      -webkit-appearance: none;
      padding-right: 30px;
      text-align: left;
      background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' fill='none' stroke='%235f5852' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 11px center;
    }

    &:disabled {
      background: var(--rm-chip-bg);
      color: var(--rm-faint);
    }
  }

  td {
    vertical-align: top;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .sp-err {
    display: block;
    margin-top: 3px;
    text-align: right;
  }

  .sp-name {
    font-weight: 600;
  }

  tr.is-off .sp-name {
    text-decoration: line-through;
  }

  tr.sp-changed {
    background: #fffbf2;

    td:first-child {
      box-shadow: inset 3px 0 0 #d97706;
    }
  }

  tr.sp-extra td {
    padding-top: 0;
    background: #fffbf2;
  }
}

.sp-extra__grid {
  display: grid;
  grid-template-columns: 1fr 1.4fr 2fr;
  gap: 10px;
}

.sp-hint {
  padding: 8px 18px 12px;
  font-size: 12.5px;
  color: var(--rm-faint);
}

/* kartice (telefon) */
.sp-cards {
  padding: 12px;
}

.sp-item {
  cursor: default;
  gap: 10px;

  &:hover {
    background: var(--rm-surface);
  }

  &__head {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  &__title {
    display: grid;
    min-width: 0;

    b {
      overflow-wrap: anywhere;
    }

    .is-off {
      text-decoration: line-through;
      color: var(--rm-faint);
    }
  }

  &__req {
    display: grid;
    flex: none;
    text-align: right;

    small {
      color: var(--rm-muted);
      font-size: 11px;
    }
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
}

/* zbir */
.sp-totals {
  display: grid;
  gap: 4px;
  justify-items: end;
  padding: 14px 18px;
  border-top: 1px solid var(--rm-divider);
  background: #faf8f6;
  border-radius: 0 0 14px 14px;

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;

    &--main {
      font-size: 18px;

      b {
        font-size: 20px;
      }
    }
  }

  &__note {
    font-size: 12.5px;
  }
}

/* isporuka */
.sp-date-chips {
  margin-top: 6px;
}

.sp-gap {
  margin-top: 8px;
}

.sp-textarea {
  width: 100%;
  min-height: 80px;
  padding: 10px 11px;
  border: 1px solid var(--rm-border-strong);
  border-radius: 10px;
  background: #fff;
  color: var(--rm-text);
  font-family: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus-visible {
    outline: 2px solid var(--rm-accent);
    outline-offset: 1px;
  }
}

.sp-foot {
  text-align: center;
  font-size: 12.5px;
}

/* završni ekran */
.sp-done {
  &__icon {
    color: var(--rm-muted);

    &.is-ok {
      background: var(--rm-success-soft);
      color: var(--rm-success);
    }
  }

  &__kpis {
    width: 100%;
    max-width: 640px;
    margin-top: 14px;
    text-align: left;
  }
}

/* fiksna traka radnji */
.sp-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: #fff;
  border-top: 1px solid var(--rm-border);
  box-shadow: 0 -6px 18px rgba(28, 25, 23, 0.08);
  padding-bottom: env(safe-area-inset-bottom);

  &__in {
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 1120px;
    margin: 0 auto;
    padding: 10px 24px;
  }

  &__sum {
    display: grid;
    min-width: 0;
    line-height: 1.2;

    small {
      color: var(--rm-muted);
      font-size: 11.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    b {
      font-size: 18px;
      white-space: nowrap;
    }
  }

  &__spacer {
    flex: 1;
  }
}

.sp-reject {
  color: var(--rm-danger);
  border-color: #f1c4bf;

  &:hover:not([disabled]) {
    background: var(--rm-danger-soft);
    color: var(--rm-danger);
  }
}

/* donji list: pregled i odbijanje */
.sp-sheet {
  display: grid;
  gap: 14px;
  padding: 4px 4px 16px;
}

.sp-sheet-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  > * {
    flex: 1;
  }
}

.sp-changes {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: grid;
    gap: 2px;
    padding: 10px 12px;
    border: 1px solid var(--rm-border);
    border-left: 4px solid #d97706;
    border-radius: 10px;
  }

  span {
    color: var(--rm-muted);
    font-size: 13px;
  }
}

/* uski ekran */
.ui-compact {
  .sp-top__in,
  .sp-bar__in {
    padding-left: 14px;
    padding-right: 14px;
  }

  .sp-top__in {
    height: 56px;
    gap: 8px;
  }

  .sp-top__logo {
    width: 30px;
    height: 30px;
  }

  .sp-top__name {
    font-size: 18px;
  }

  .sp-lang button {
    flex: none;
    padding: 0 9px;
    font-size: 12px;
  }

  .sp-parties {
    grid-template-columns: 1fr;
  }

  .sp-tools {
    padding: 10px 12px;
  }

  .sp-bar__sum b {
    font-size: 16px;
  }

  .sp-bar__in {
    gap: 8px;
  }

  .sp-bar .ui-btn {
    flex: none;
    padding: 0 12px;
  }

  .sp-extra__grid {
    grid-template-columns: 1fr;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .sp.rd {
    background: #fff;
    padding-bottom: 0;
  }
}
</style>
